import { NavigateFunction } from 'react-router-dom';
import { getDgiBusinessInfo, updateBusinessExecutive } from '../services/commerce.service';
import { Contract } from '../types/add_commerce.types';
import { FormStatusEnum } from '../types/enums/form-status.enum';
import { FormStepEnum } from '../types/enums/form-step.enum';
import { isZurichChannelAllowed } from './institutionUtils';
import { getAddBusinessPage, pagesPaths } from './navigationUtils';

export interface ContinueOnboardingWizardParams {
  commerceRut: number;
  businessData: any;
  state: FormStatusEnum;
  step: FormStepEnum;
  from: string;
}

// Shared by every screen that can resume an in-progress onboarding (MyOnboardings, the
// "Sin completar" preview page) so the resume logic only lives in one place.
export const continueOnboardingWizard = async (
  navigate: NavigateFunction,
  { commerceRut, businessData, state, step, from }: ContinueOnboardingWizardParams,
) => {
  const path = getAddBusinessPage(step);

  if (state === FormStatusEnum.FREE) {
    await updateBusinessExecutive(+businessData.id);
  }

  if (!path) {
    return;
  }

  const getDgiInfo = path === pagesPaths.CommerceInformation && !Object.keys(businessData).length;

  if (getDgiInfo) {
    const dgiInfo = await getDgiBusinessInfo(commerceRut);

    if (dgiInfo.status >= 400) {
      return navigate(path, { state: { commerceRut, from } });
    }
    return navigate(path, {
      state: { commerceRut, ...businessData, ...dgiInfo.data, from },
    });
  }

  const isFinished = businessData.status === FormStatusEnum[FormStatusEnum.FINISHED];
  const contractsAllSigned: boolean = businessData?.contracts?.every(
    (contract: Contract) => contract.vaFirmaStatus === 'SIGNED' || contract.vaFirmaStatus === 'NOT_SENDED',
  );
  const targetPath = isFinished && contractsAllSigned ? pagesPaths.CommerceSummary : path;
  const hideEmailSection = targetPath === pagesPaths.CommerceSummary && isFinished;

  // Resuming can jump straight past CommerceTerminals (whose own submit-time guard never
  // runs), so a Zurich insurance persisted under a previously eligible channel/role must be
  // stripped here too before it reaches any later step.
  const sanitizedBusinessData =
    !isZurichChannelAllowed() && businessData?.insurances?.length > 0
      ? { ...businessData, insurances: [] }
      : businessData;

  navigate(targetPath, {
    state: { commerceRut, ...sanitizedBusinessData, from, hideEmailSection },
  });
};
